import * as ko from "knockout";

import pliCall, { PliCallPromise, PliCallResponse, PliCallErrorData } from "../core/call";
import * as pliObservable from "../forms-knockoutcontrols";

import * as shared from "./signInShared";
import { SignInPageMain } from "./signInPageMain";


/**
 * Settings for "SignInPagePasswordReset.activate" calls.
 */
export interface SignInPageChangePasswordActivationSettings {
    /**
     * How many milliseconds after activation should the initial focus be set.
     * When not defined, defaults to 500 milliseconds (default page transition animation duration).
     * When <= 0, focus is set instantly.
     */
    focusDelay?: number;

    /**
     * Name of the user that has to change his/her password. Required.
     */
    username: string;

    /**
     * Server response that caused change-password page to be activated.
     * Stored in "serverData" observable which is used by view to allow
     * server to override built-in texts and links.
     */
    serverData?: any;
}


/**
 * Viewmodel for sign-in page that forces user to change their password.
 */
export class SignInPageChangePassword {

    constructor(owner: shared.SignInPagesCollection, pageId: shared.SignInPageId) {
        this._owner = owner;
        this._pageId = pageId;
    }


    // -------------------------------------------------------------------------
    // Viewmodel properties
    // -------------------------------------------------------------------------

    /** Computed flag that will switch to true while busy indicator (spinner) should be displayed. */
    busy: ko.PureComputed<boolean> = ko.pureComputed(this._calculateBusy, this);

    /** Flag indicating that password has been changed successfully (when true). */
    done: ko.Observable<boolean> = ko.observable(false);

    /** Message to display when password is successfully changed. */
    doneMessage: ko.Observable<string> = ko.observable("");

    /** Error message currently being displayed. */
    error: ko.Observable<string> = ko.observable("");

    /** Tracks old password entered by user. */
    oldPassword: shared.TextBoxObservableWithFocus<string> =
        shared.createTextBoxObservableWithFocus("", null, pliObservable.behaviours.required);

    /** Tracks new password entered by user. */
    newPassword: shared.TextBoxObservableWithFocus<string> =
        shared.createTextBoxObservableWithFocus("", null, pliObservable.behaviours.required);

    /** Tracks repeated new password entered by user (to guard against mis-spelling). */
    newPasswordAgain: shared.TextBoxObservableWithFocusAndVisibility<string> =
        shared.createTextBoxObservableWithFocusAndVisibility("", { source: this.newPassword }, pliObservable.behaviours.repeatedValue);

    /** Server response that caused change-password-page to be activated. */
    serverData: ko.Observable<any> = ko.observable(shared.emptyData);

    /** Flag tracking focus of "submit" button. */
    submitFocus: ko.Observable<boolean> = ko.observable(false);


    // -------------------------------------------------------------------------
    // Viewmodel actions
    // -------------------------------------------------------------------------

    /**
     * Transition to this page + set initial focus.
     */
    activate(settings: SignInPageChangePasswordActivationSettings): void {
        // validate settings
        if (!settings) { throw new Error("SignInPageChangePassword.activate requires settings object"); }
        if (typeof settings.username !== "string") { throw new Error("SignInPageChangePassword.activate requires settings.username string"); }
        let focusDelay = 500; // default, duration of page transition
        if (settings.focusDelay !== undefined) { focusDelay = settings.focusDelay; }

        // set initial property values
        this._call(null);
        this._username = settings.username;
        this.done(false);
        this.doneMessage("");
        this.error("");
        this.oldPassword.reset(false, "", "");
        this.newPassword.reset(false, "", "");
        this.newPasswordAgain.visible(true);
        this.newPasswordAgain.reset(false, "", "");
        this.serverData(settings.serverData || shared.emptyData);

        // initiate page transition
        this._owner.currentPageId(this._pageId);

        // set initial focus
        if (focusDelay > 0) {
            setTimeout(() => this.oldPassword.hasFocus(true), focusDelay);
        } else {
            this.oldPassword.hasFocus(true);
        }
    }

    /**
     * Validate viewmodel + initiate server call to change user's password.
     */
    submit(): void {
        if (this.busy()) { return; }

        if (this.done()) {
            const mainPage = this._owner.getPage<SignInPageMain>(shared.SignInPageId.Main);
            mainPage.activate({ transition: -1 });
            return;
        }

        this.oldPassword.validate();
        this.newPassword.validate();
        if (this.newPasswordAgain.visible()) {
            this.newPasswordAgain.validate();
        } else {
            this.newPasswordAgain.setErrorMessage(null);
        }

        let errorMessage: string = null;
        if (this.oldPassword.isValid() && this.newPassword.isValid() && this.newPasswordAgain.isValid()) {
            if (this.oldPassword() === this.newPassword()) {
                errorMessage = "New password must be different from old password";
                this.newPasswordAgain.reset(false, "", "");
                this.newPassword.reset(false, "", "");
                this.newPassword.setErrorMessage("Must be different from old password");
                this.newPassword.hasFocus(true);
            }
        } else if (!this.oldPassword.isValid() && !this.newPassword.isValid()) {
            errorMessage = "Missing current and new passwords";
            this.oldPassword.hasFocus(true);
        } else if (!this.oldPassword.isValid()) {
            errorMessage = "Missing current password";
            this.oldPassword.hasFocus(true);
        } else if (!this.newPassword.isValid()) {
            errorMessage = "Missing new password";
            this.newPassword.hasFocus(true);
        } else if (!this.newPasswordAgain.isValid()) {
            errorMessage = "Repeated new password does not match";
            this.newPasswordAgain.hasFocus(true);
        }
        if (errorMessage) {
            this.error(errorMessage + ".\nPlease try again.");
            return;
        }

        this.error("");
        this._call(pliCall({
            owner: this,
            name: "Pli-Authentication-ChangePassword",
            data: {
                username: this._username,
                oldPassword: this.oldPassword(),
                newPassword: this.newPassword()
            }
        }));
        this._call().always(SignInPageChangePassword._processResponse);
    }

    /**
     * Toggles value of "newPasswordAgain.visible" + sets "newPassword.hasFocus".
     */
    togglePasswordVisibility(): void {
        this.newPasswordAgain.visible(!this.newPasswordAgain.visible());
        this.newPassword.hasFocus(true);
    }


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Determines when this viewmodel is busy.
     */
    private _calculateBusy(): boolean {
        return (!!this._call());
    }

    /**
     * Processes result of change-password server call ("Pli-Authentication-ChangePassword").
     */
    private static _processResponse(response: PliCallResponse<any, SignInPageChangePassword>): void {
        const  thisPage = response.owner;
        if (thisPage._call().xhr !== response.clientXhr) {
            console.warn("Ignoring response ", response);
            return;
        }

        let focus: ko.Observable<boolean> = null;
        if (response.isError) {
            const errorData = response.result as PliCallErrorData;
            thisPage.error(errorData.message + ".\nPlease try again.");

            focus = thisPage.oldPassword.hasFocus;
            if (errorData.validationErrors) {
                let message: string;
                message = errorData.validationErrors["newPassword"];
                if (message) { thisPage.newPassword.setErrorMessage(message); focus = thisPage.newPassword.hasFocus; }
                message = errorData.validationErrors["oldPassword"];
                if (message) { thisPage.oldPassword.setErrorMessage(message); focus = thisPage.oldPassword.hasFocus; }
            }
        } else {
            focus = thisPage.submitFocus;
            thisPage.doneMessage((response.result && response.result.message) || "Your password was successfully changed.");
            thisPage.done(true);
        }

        thisPage._call(null);
        if (focus) { focus(true); }
    }

    /** Reference to the current server call (sign-in request). */
    private _call: ko.Observable<PliCallPromise<any, SignInPageChangePassword>> = ko.observable(null); // readonly (observable)

    /** Collection that owns this page. Used for page transitions. */
    private _owner: shared.SignInPagesCollection; // readonly, initialized in constructor

    /** ID of the page that was instantiated with this viewmodel instance. */
    private _pageId: shared.SignInPageId; // readonly, initialized in constructor

    /** Username specified in the last "activate" call. */
    private _username: string; // updated in "activate"

}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms-signin/signInPageChangePassword.ts